import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Receipt, Link2, Mail, RefreshCw, Download, Copy } from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const STATUS_TONE = { paid: 'bg-emerald-100 text-emerald-800', sent: 'bg-sky-100 text-sky-800', draft: 'bg-stone-100 text-stone-700' };

const Line = ({ label, value, strong, testid }) => (
  <div className={`flex justify-between py-1.5 ${strong ? 'border-t border-stone-200 mt-1 pt-2.5 font-bold text-stone-900' : 'text-stone-700'}`}>
    <span>{label}</span><span className="font-mono" data-testid={testid}>{value}</span>
  </div>
);

const InvoiceRow = ({ inv, onRefresh }) => {
  const [busy, setBusy] = useState('');
  const act = async (kind) => {
    setBusy(kind);
    try {
      if (kind === 'status') {
        const r = await axios.post(`${API}/consultancy-billing/invoices/${inv.id}/check-status`, {}, { headers: headers() });
        toast.success(r.data.recorded ? `Paid — ${inr(r.data.amount)} recorded in the project ledger` : `Razorpay status: ${r.data.status}`);
      } else if (kind === 'email') {
        await axios.post(`${API}/consultancy-billing/invoices/${inv.id}/email`, {}, { headers: headers() });
        toast.success('Invoice emailed to client');
      } else if (kind === 'pdf') {
        const r = await axios.get(`${API}/invoices/${inv.id}/export/pdf`, { headers: headers(), responseType: 'blob' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(r.data);
        a.download = `${inv.invoice_number}.pdf`;
        a.click();
      }
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setBusy('');
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 py-2 border-b border-stone-100 last:border-0 text-xs" data-testid={`consultancy-invoice-${inv.id}`}>
      <span className="font-mono font-semibold text-stone-900">{inv.invoice_number}</span>
      <span className="text-stone-500">S{inv.consultancy_stage}</span>
      <span className="font-mono">{inr(inv.total)}</span>
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${STATUS_TONE[inv.status] || STATUS_TONE.draft}`} data-testid={`consultancy-invoice-status-${inv.id}`}>{inv.status}</span>
      <div className="ml-auto flex gap-1">
        {inv.payment_link_url && (
          <Button size="sm" variant="ghost" className="h-7 px-2" title="Copy payment link" data-testid={`consultancy-invoice-copy-${inv.id}`}
            onClick={() => { navigator.clipboard.writeText(inv.payment_link_url); toast.success('Payment link copied'); }}><Copy size={13} /></Button>
        )}
        <Button size="sm" variant="ghost" className="h-7 px-2" disabled={!!busy} onClick={() => act('pdf')} title="Download PDF" data-testid={`consultancy-invoice-pdf-${inv.id}`}><Download size={13} /></Button>
        <Button size="sm" variant="ghost" className="h-7 px-2" disabled={!!busy} onClick={() => act('email')} title="Email to client" data-testid={`consultancy-invoice-email-${inv.id}`}><Mail size={13} /></Button>
        {inv.razorpay_link_id && inv.status !== 'paid' && (
          <Button size="sm" variant="outline" className="h-7 px-2.5" disabled={!!busy} onClick={() => act('status')} data-testid={`consultancy-invoice-check-${inv.id}`}>
            <RefreshCw size={12} className={`mr-1 ${busy === 'status' ? 'animate-spin' : ''}`} /> Check status
          </Button>
        )}
      </div>
    </div>
  );
};

export const ConsultancyInvoiceDialog = ({ open, onOpenChange, worksheetId, onPaymentRecorded }) => {
  const [preview, setPreview] = useState(null);
  const [withLink, setWithLink] = useState(true);
  const [sendEmail, setSendEmail] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/consultancy-billing/${worksheetId}/preview`, { headers: headers() })
      .then((r) => setPreview(r.data))
      .catch((err) => { toast.error(err.response?.data?.detail || 'Could not load fee preview'); setPreview(false); });
  }, [worksheetId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const create = async () => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/consultancy-billing/${worksheetId}/invoice`, { create_payment_link: withLink, send_email: sendEmail }, { headers: headers() });
      toast.success(`Invoice ${r.data.invoice.invoice_number} created${r.data.invoice.payment_link_url ? ' with Razorpay link' : ''}${r.data.emailed ? ' and emailed' : ''}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreating(false);
    }
  };

  const refresh = () => { load(); onPaymentRecorded?.(); };
  const p = preview || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="consultancy-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Receipt size={18} /> Invoice consultancy stage</DialogTitle>
          <DialogDescription>Bills the locked stage's consultancy fee, less anything invoiced earlier.</DialogDescription>
        </DialogHeader>
        {preview === null ? <div className="h-32 animate-pulse rounded-xl bg-stone-100" /> : preview === false ? null : (
          <div className="space-y-4">
            <div className="rounded-xl bg-emerald-50/70 border border-emerald-200 px-4 py-3 text-sm" data-testid="consultancy-invoice-stage">
              <b>Stage {p.stage || 0}</b>{p.stage_label ? ` · ${p.stage_label}` : ''} · {p.pct || 0}% of consultancy fee {inr(p.fee)}
            </div>
            <div className="text-sm">
              <Line label="Cumulative fee due" value={inr(p.fee_due)} testid="consultancy-invoice-fee-due" />
              <Line label="Already invoiced" value={`− ${inr(p.already_invoiced)}`} testid="consultancy-invoice-already" />
              <Line label="Due now" value={inr(p.amount_due_now)} testid="consultancy-invoice-due-now" />
              <Line label={`GST (${p.gst_pct || 18}%)`} value={inr(p.gst)} testid="consultancy-invoice-gst" />
              <Line label="Invoice total" value={inr(p.total)} strong testid="consultancy-invoice-total" />
            </div>
            <div className="space-y-2 text-sm">
              <label className="flex items-center gap-2"><Checkbox checked={withLink} onCheckedChange={(v) => setWithLink(!!v)} data-testid="consultancy-invoice-link-toggle" /> <Link2 size={14} /> Create Razorpay payment link</label>
              <label className="flex items-center gap-2"><Checkbox checked={sendEmail} onCheckedChange={(v) => setSendEmail(!!v)} data-testid="consultancy-invoice-email-toggle" /> <Mail size={14} /> Email invoice PDF + Pay Now link to client</label>
            </div>
            {p.invoices?.length > 0 && (
              <div className="rounded-xl border border-stone-200 px-3 py-1" data-testid="consultancy-invoice-list">
                {p.invoices.map((inv) => <InvoiceRow key={inv.id} inv={inv} onRefresh={refresh} />)}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="consultancy-invoice-close-btn">Close</Button>
          <Button onClick={create} disabled={creating || !p.stage || !(p.amount_due_now > 0)} data-testid="consultancy-invoice-create-btn">
            {creating ? 'Creating…' : `Create invoice${p.total ? ` · ${inr(p.total)}` : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
